import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useUser } from '../../../shared/hooks';
import { useParams } from 'react-router';
import clsx from 'clsx';
import { BookmarkModeEnum } from '../../../shared/constants';
import { useTheme } from '@material-ui/core';
import { FontAwesomeSpriteButton } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import {
  useAddStudentBookmarkMutation,
  useGetStudentBookmarkQuery,
} from 'src/modules/shared/slices/bookmark/api/bookmarkApi';
import { UrlParams } from 'src/modules/shared/types';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import {
  resetScroll,
  setBookmarkMode,
} from 'src/modules/shared/slices/bookmark/entity/bookmarkEntity';
import { handleScroll } from './helpers';

type StudentBookmarkIndicatorProps = {
  blockId: string;
  className?: string;
};

export const StudentBookmarkIndicator: FC<StudentBookmarkIndicatorProps> = ({
  blockId,
  className,
}) => {
  // hooks
  const dispatch = useAppDispatch();
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);
  const theme = useTheme();
  const { isStudent } = useUser();
  const log = useDataLogging();
  const { bookId } = useParams<UrlParams>();
  // rtk
  const [addBookmark, response] = useAddStudentBookmarkMutation();
  const { data: studentBookmarksData, refetch } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  const scroll = useAppSelector((state) => state.bookmark.scroll);

  const bookmark = useMemo(() => {
    if (studentBookmarksData === undefined) return undefined;
    return studentBookmarksData;
  }, [studentBookmarksData]);

  const isBookmarked =
    bookmark &&
    Array.isArray(studentBookmarksData) &&
    bookmark.find((item) => item.blockId === blockId);

  useEffect(() => {
    if (!scroll?.shouldScroll && !scroll?.blockId) return;
    handleScroll(scroll?.blockId);
    dispatch(resetScroll());
  }, [dispatch, scroll?.blockId, scroll?.shouldScroll]);

  const handleClick = useCallback(
    (blockId: string) => {
      if (bookId) {
        addBookmark({ bookId, blockId });
      }
    },
    [addBookmark, bookId]
  );

  useEffect(() => {
    if (!response.isSuccess) return;
    if (response.isSuccess) {
      dispatch(setBookmarkMode(BookmarkModeEnum.DISABLED));
      log(LogActionTypes.STUDENT_CREATE_BOOKMARK, {
        bookId,
        blockId,
      });
      response.reset();
      refetch();
    }
  }, [blockId, bookId, dispatch, log, refetch, response]);

  return (
    <div className={clsx(className, { active: isBookmarked })}>
      {(bookmarkMode === BookmarkModeEnum.SELECT_BLOCK || isBookmarked) && (
        <FontAwesomeSpriteButton
          buttonStyle={{ marginTop: 0 }}
          icon={SpriteIconsLight.BOOKMARK}
          color={isBookmarked ? theme.palette.common.white : '#c6c0b3'}
          iconSize="md"
          onClick={() => handleClick(blockId)}
        />
      )}
    </div>
  );
};
